import { useState, useEffect } from 'react';
import { Bell, X } from 'lucide-react';
import { requestNotificationPermission } from '../../lib/messaging';

const STORAGE_KEY = 'notif_banner_dismissed';

export default function NotificationBanner() {
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Only show once per user — dismissed state survives across sessions
    if (!('Notification' in window)) return;
    if (localStorage.getItem(STORAGE_KEY)) return;
    // Wait a beat so the page paints first, then slide in
    const showTimer = setTimeout(() => {
      if (Notification.permission === 'default') setVisible(true);
    }, 800);
    // Auto-dismiss after 10s if user hasn't interacted
    const dismissTimer = setTimeout(() => {
      localStorage.setItem(STORAGE_KEY, '1');
      setVisible(false);
    }, 10_800);
    return () => { clearTimeout(showTimer); clearTimeout(dismissTimer); };
  }, []);

  const handleEnable = async () => {
    setBusy(true);
    try {
      await requestNotificationPermission();
    } finally {
      setBusy(false);
      // Hide and remember regardless of outcome (granted, denied, or error)
      localStorage.setItem(STORAGE_KEY, '1');
      setVisible(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, '1');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="animate-slide-down border-b border-blue-200/60 bg-gradient-to-r from-blue-50 via-sky-50 to-indigo-50">
      <div className="mx-auto flex items-center justify-between gap-4 px-4 sm:px-6 py-3">
        <div className="flex items-center gap-3 min-w-0">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-brand">
            <Bell className="h-4 w-4" />
          </span>
          <p className="text-xs sm:text-sm font-semibold text-slate-800 leading-snug">
            🔔 Enable notifications to get updates on your application status
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={handleEnable}
            disabled={busy}
            className="rounded-xl btn-primary px-4 py-2 text-xs font-bold text-white shadow-sm  active:scale-[0.97] transition-all cursor-pointer disabled:opacity-60"
          >
            {busy ? (
              <span className="flex items-center gap-1.5">
                <span className="inline-block h-3 w-3 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                Enabling…
              </span>
            ) : (
              'Enable'
            )}
          </button>
          <button
            onClick={handleDismiss}
            className="rounded-xl px-3 py-2 text-xs font-bold text-slate-500 hover:text-slate-700 hover:bg-white/60 transition-all cursor-pointer"
          >
            Not now
          </button>
          <button
            onClick={handleDismiss}
            className="flex h-6 w-6 items-center justify-center rounded-full text-slate-400 hover:text-slate-600 hover:bg-white/60 transition-all cursor-pointer"
            aria-label="Dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
